/* global G */
import { Grid } from '@mui/material'
import useStyles from 'platform/react/hook/useStyles'
import { getThemeColor } from 'lib/util'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { formatDigits } from 'lib/util/string'
import Text from 'ui/Element/Text'
import PlatformEvent from 'lib/util/event'
import ApplicationContext from 'platform/react/context/application'

const SIZE = '1rem'
const DOT_SIZE = '0.5rem'

const styles = (theme, {
  horizontalOffset,
  verticalOffset,
  anchor,
  backgroundColor,
  border,
  dot,
  iconColor,
  borderColor,
}) => ({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  text: {
    lineHeight: '0.75rem', // same as fontsize
  },
  badge: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 0.25rem',

    zIndex: 10,
    position: 'absolute',

    ...anchor?.vertical === 'top' && {
      top: verticalOffset,
    },
    ...anchor?.vertical === 'bottom' && {
      bottom: verticalOffset,
    },

    ...anchor?.horizontal === 'right' && {
      right: horizontalOffset,
    },
    ...anchor?.horizontal === 'left' && {
      left: horizontalOffset,
    },

    minWidth: dot ? DOT_SIZE : SIZE,
    height: dot ? DOT_SIZE : SIZE,
    borderRadius: '0.75rem', // not 100%, or we'll have an egg with more than 1 digit
    outline: border || dot
      ? `0.125rem solid ${getThemeColor(theme, iconColor === 'white.main' ? 'gray.960' : borderColor)}`
      : 'none',
    backgroundColor: getThemeColor(theme, backgroundColor),
  },
})

/**
 * @typedef {Object} Anchor
 * @property {"top" | "bottom"} vertical
 * @property {"left" | "right"} horizontal
 */

/**
 * Badge Element
 *
 * Displays a badge around `children`.
 *
 * @param {Object} p
 * @param {string|number} p.value                   the value to display
 * @param {boolean} [p.dot=false]                   whether the badge should be presented as a dot
 * @param {boolean} [p.border=false]                whether the badge should have a border
 * @param {boolean} [p.bold=false]                  whether the badge should have bold text
 * @param {string} [p.color='white.main']           color of `value`
 * @param {string} [p.backgroundColor='error.700']  color of the badge
 * @param {string} [p.borderColor='white.main']     color of the border
 * @param {?string} [p.iconColor=null]              optional color if the badge surrounds an icon
 *                                                  or button. This is needed because on a white
 *                                                  background the border should be not white.
 * @param {number} [p.numberOfDigits=1]             the maximum number of digits to display
 * @param {number} [p.horizontalOffset=5]           vertical offset for the badge
 * @param {number} [p.verticalOffset=5]             horizontal offset for the badge
 * @param {Anchor} [p.anchor]                       anchor of the badge in relation to the parent
 * @param {Gaia.Component.Events} p.events          events for the component
 * @param {?string} [p.group]                       an optional attachment group to display the count of
 * @param {React.ReactNode[]} p.children            the children to print
 * @returns {JSX.Element}
 */
const Badge = ({
  value: initialValue,
  dot = false,
  bold = false,
  border = false,
  color = 'white.main',
  backgroundColor = 'error.700',
  borderColor = 'white.main',
  iconColor = null,
  numberOfDigits = 1,
  horizontalOffset = 2,
  verticalOffset = 5,
  anchor = {
    vertical: 'top',
    horizontal: 'right',
  },
  events,
  group = null,
  children,
}) => {
  const { eventBus } = useContext(ApplicationContext)
  const [value, setValue] = useState(initialValue || 0)

  const handleAttachment = useCallback((e) => {
    const count = (e?.detail?.[G.DATA] || []).length
    count > 0 && setValue(count)
  }, [])

  useEffect(() => {
    (async () => {
      const data = await events?.onOpen?.(new PlatformEvent('open', { value: initialValue, numberOfDigits }))
      data && setValue(data)
    })()

    if (group) {
      eventBus.add(eventBus.type(G.ATTACHMENT, G.DONE, group), handleAttachment)
      eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, group), { [G.DATA]: {} })
    }

    return () => {
     group && eventBus.remove(eventBus.type(G.ATTACHMENT, G.DONE, group), handleAttachment)
    }
  }, [])

  useEffect(() => {
    events?.onChange?.(new PlatformEvent('change', { value }))
  }, [value])

  useEffect(() => { setValue(initialValue) }, [initialValue])

  const text = useMemo(() => formatDigits(value, numberOfDigits), [value, numberOfDigits])
  const classes = useStyles(styles, {
    dot,
    border,
    backgroundColor,
    iconColor,
    borderColor,
    text,
    anchor,
    horizontalOffset,
    verticalOffset,
  })()

  return (
    <Grid className={classes.container}>
      {value > 0 && (
        <Grid className={classes.badge}>
          {!dot && (
            <Text
              value={text}
              color={color}
              variant={bold ? '12/bold' : '12/medium'}
              className={classes.text}
            />
          )}
        </Grid>
      )}
      {children}
    </Grid>
  )
}

export default Badge
